/**
 * Onboarding's three form steps — *Your firm*, *Address & hours*, *Billing
 * defaults* (w12b, C11, FR-A9, T-1.22).
 *
 * **12A's fields and rules, not a second copy of them.** The state list, the
 * identifier shapes, the GSTIN-against-state-and-PAN check, the month names,
 * the place-of-supply options and the working-hours editor are all
 * `routes/Firm/settings/`'s — so a firm is told the same thing, in the same
 * words, here and on *Your firm* later. Each step is a controlled component:
 * the flow (`Onboarding.tsx`) owns the draft and what is sent.
 */

import { Input, Select } from '@fiveox/ui'
import { Sparkles } from 'lucide-react'
import type { ReactNode } from 'react'

import { invoiceNumberExample } from '../../../lib/api/billing'
import type { FirmProfile, FirmSettings } from '../../../lib/api/firm'
import {
  MONTHS,
  STATES,
  fromSupplyValue,
  gstinProblem,
  monthName,
  monthNumber,
  placeOfSupplyOptions,
  shapeProblem,
  toSupplyValue,
} from '../settings/rules'
import { WorkingHoursEditor } from '../settings/WorkingHoursEditor'

type Field = keyof FirmProfile

interface StepProps {
  draft: Partial<FirmProfile>
  set: (field: Field, value: unknown) => void
  disabled?: boolean
  /** Fields the platform's organisation filled — marked so the firm checks them. */
  prefilled: Field[]
}

function Group({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <fieldset className="space-y-4">
      <legend className="mb-1">
        <span className="block text-sm font-semibold text-foreground">{title}</span>
        {hint && <span className="block text-xs text-muted-foreground">{hint}</span>}
      </legend>
      {children}
    </fieldset>
  )
}

const FROM_ORG = 'From your organisation — check it.'

function text(
  { draft, set, disabled, prefilled }: StepProps,
  field: Field,
  label: string,
  extra: Record<string, unknown> = {},
) {
  const hint = prefilled.includes(field) ? FROM_ORG : (extra.hint as string | undefined)
  return (
    <Input
      label={label}
      value={(draft[field] as string | null) ?? ''}
      disabled={disabled}
      onChange={(e) => set(field, e.target.value)}
      {...extra}
      hint={hint}
    />
  )
}

// -- Your firm ----------------------------------------------------------------

/** What *Your firm* refuses before the request, or `null`. */
export function firmStepProblem(draft: Partial<FirmProfile>): string | null {
  return (
    shapeProblem('pan', draft.pan) ??
    shapeProblem('gstin', draft.gstin) ??
    shapeProblem('tan', draft.tan) ??
    gstinProblem({ gstin: draft.gstin, state: draft.state, pan: draft.pan })
  )
}

export function FirmStep(props: StepProps) {
  const { draft, set, disabled, prefilled } = props
  return (
    <div className="space-y-8">
      <Group title="Names and contact" hint="What your invoices, website and messages say.">
        <div className="grid gap-4 sm:grid-cols-2">
          {text(props, 'legal_name', 'Legal name', {
            required: true,
            autoComplete: 'organization',
            hint: 'As registered — printed on every invoice.',
          })}
          {text(props, 'display_name', 'Display name', {
            hint: 'What clients call you, if different. Optional.',
          })}
          {text(props, 'email', 'Email', {
            required: true,
            type: 'email',
            inputMode: 'email',
            autoComplete: 'email',
            hint: 'Where client messages and enquiries reach you.',
          })}
          {text(props, 'mobile', 'Mobile', { type: 'tel', inputMode: 'tel', hint: 'Optional.' })}
        </div>
      </Group>

      <Group
        title="Registrations"
        hint="Each is optional and can be added later. A GSTIN must agree with your PAN."
      >
        <div className="grid gap-4 sm:grid-cols-3">
          {(['pan', 'gstin', 'tan'] as const).map((field) => (
            <Input
              key={field}
              label={field.toUpperCase()}
              value={(draft[field] as string | null) ?? ''}
              disabled={disabled}
              maxLength={field === 'gstin' ? 15 : 10}
              autoCapitalize="characters"
              spellCheck={false}
              className="font-mono tracking-wide"
              hint={prefilled.includes(field) ? FROM_ORG : 'Your firm’s own.'}
              error={
                shapeProblem(field, draft[field]) ??
                (field === 'gstin'
                  ? gstinProblem({ gstin: draft.gstin, state: draft.state, pan: draft.pan })
                  : null)
              }
              onChange={(e) => set(field, e.target.value.toUpperCase())}
            />
          ))}
        </div>
      </Group>
    </div>
  )
}

// -- Address & hours ----------------------------------------------------------

/** The state is checked against the GSTIN **as saved** — what the service
 *  compares it with. */
export function addressStepProblem(
  draft: Partial<FirmProfile>,
  saved: Partial<FirmProfile>,
): string | null {
  return gstinProblem({ gstin: saved.gstin, state: draft.state, pan: saved.pan })
}

export function AddressStep(props: StepProps & { saved: Partial<FirmProfile> }) {
  const { draft, set, disabled, prefilled, saved } = props
  return (
    <div className="space-y-8">
      <Group title="Office address" hint="Your registered office. The state decides your place of supply.">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            {text(props, 'address_line1', 'Address line 1', { autoComplete: 'address-line1' })}
          </div>
          <div className="sm:col-span-2">
            {text(props, 'address_line2', 'Address line 2', { autoComplete: 'address-line2' })}
          </div>
          {text(props, 'city', 'City', { autoComplete: 'address-level2' })}
          <Select
            label="State or union territory"
            value={draft.state ?? ''}
            disabled={disabled}
            hint={prefilled.includes('state') ? FROM_ORG : undefined}
            error={addressStepProblem(draft, saved)}
            onChange={(e) => set('state', e.target.value || null)}
          >
            <option value="">Choose a state</option>
            {STATES.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </Select>
          {text(props, 'pincode', 'PIN code', { inputMode: 'numeric', maxLength: 10 })}
          {text(props, 'landline', 'Office landline', {
            type: 'tel',
            inputMode: 'tel',
            maxLength: 20,
            hint: 'Optional.',
          })}
        </div>
      </Group>

      <Group title="Working hours" hint="When the office is open. Your website’s footer can show them.">
        <WorkingHoursEditor
          value={draft.working_hours ?? null}
          disabled={disabled}
          onChange={(next) => set('working_hours', next)}
        />
      </Group>
    </div>
  )
}

// -- Billing defaults ---------------------------------------------------------

export function billingStepProblem(settings: Partial<FirmSettings>): string | null {
  return (settings.invoice_series_prefix ?? '').trim() ? null : 'An invoice series needs a prefix.'
}

export function BillingStep({
  settings,
  setSetting,
  firmState,
  disabled,
  today,
}: {
  settings: Partial<FirmSettings>
  setSetting: <K extends keyof FirmSettings>(field: K, value: FirmSettings[K]) => void
  firmState: string | null
  disabled?: boolean
  /** For tests; the example number is for this financial year. */
  today?: Date
}) {
  const prefix = settings.invoice_series_prefix ?? ''
  const month = settings.financial_year_start_month ?? 4
  const example = invoiceNumberExample(prefix, month, today)
  return (
    <div className="space-y-8">
      {/* The example is the whole point of asking (Wave 5 close §3.5): a
          doubled `INV` is obvious the moment it is on screen and invisible
          until then. */}
      <div
        className="relative overflow-hidden rounded-2xl border border-primary/30 bg-primary/5 p-5"
        data-invoice-example=""
      >
        <Sparkles
          size={72}
          className="pointer-events-none absolute -right-3 -top-3 text-primary/10"
          aria-hidden
        />
        <p className="text-xs font-semibold uppercase tracking-wide text-primary">
          Your first invoice will be numbered
        </p>
        <p
          className="mt-2 break-all font-mono text-2xl font-semibold text-foreground sm:text-3xl"
          aria-live="polite"
        >
          {example}
        </p>
        <p className="mt-2 text-xs text-muted-foreground">
          Prefix · financial year · series · number. It changes as you type.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Input
          label="Invoice number prefix"
          value={prefix}
          maxLength={16}
          disabled={disabled}
          className="font-mono tracking-wide"
          autoCapitalize="characters"
          spellCheck={false}
          error={billingStepProblem(settings)}
          hint="Most firms use their initials. It is frozen once a financial year issues its first invoice."
          onChange={(e) => setSetting('invoice_series_prefix', e.target.value.toUpperCase())}
        />
        <Select
          label="Financial year starts in"
          value={monthName(month)}
          disabled={disabled}
          hint="April for almost every practice in India."
          onChange={(e) => setSetting('financial_year_start_month', monthNumber(e.target.value) ?? 4)}
        >
          {MONTHS.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </Select>
        <div className="sm:col-span-2">
          <Select
            label="Default place of supply"
            value={toSupplyValue(settings.default_place_of_supply)}
            disabled={disabled}
            hint={
              firmState
                ? `A client in ${firmState} is billed CGST and SGST; one elsewhere, IGST.`
                : 'Decides CGST and SGST or IGST. “The firm’s own state” follows the state on your address.'
            }
            onChange={(e) => setSetting('default_place_of_supply', fromSupplyValue(e.target.value))}
          >
            {placeOfSupplyOptions(firmState).map((option) => (
              <option key={option.value || 'own'} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </div>
      </div>
    </div>
  )
}
